import { useEffect, useRef, useState, type ReactNode } from 'react';
import { PanelLeftClose, Menu } from 'lucide-react';
import type { ChapterEntry } from '../data/rewards-store-chapters';
import { useScrollProgress } from '../hooks/useScrollProgress';

interface EbookShellProps {
  /** Chapter definitions used for sidebar navigation and scroll tracking. */
  chapters: ChapterEntry[];
  /** Rendered at the top of the sidebar (e.g., logo + back link). */
  sidebarHeader?: ReactNode;
  /**
   * Render function for the mobile top bar.
   * Receives sidebar state so the consuming app can wire up a menu toggle button.
   */
  mobileHeader?: (props: {
    sidebarOpen: boolean;
    onToggleSidebar: () => void;
  }) => ReactNode;
  /**
   * URL the persistent "Back to Website" pill links to on desktop.
   * Default: "/".
   */
  backHref?: string;
  /**
   * Label shown next to the back arrow in the persistent desktop pill.
   * Default: "Back to Website".
   */
  backLabel?: string;
  /** Rendered after the main content area (e.g., CTA section). */
  footer?: ReactNode;
  /** The ebook content (chapter sections). */
  children: ReactNode;
}

/**
 * Shared layout shell for ebook pages.
 *
 * Provides:
 * - A reading progress bar fixed at the top of the viewport
 * - A sidebar with chapter navigation (table of contents)
 * - A mobile sidebar overlay with toggle support
 * - Slot-based composition for app-specific headers, footers, and content
 */
export function EbookShell({
  chapters,
  sidebarHeader,
  mobileHeader,
  backHref = '/',
  backLabel = 'Back to Website',
  footer,
  children,
}: EbookShellProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(false);
  const { activeSection, navRef } = useScrollProgress(chapters);
  const mobileHeaderRef = useRef<HTMLDivElement | null>(null);
  const mobileSidebarRef = useRef<HTMLElement | null>(null);

  // Measure the consuming app's mobile subheader and expose its height as a
  // CSS variable so the mobile drawer can sit flush below it without
  // depending on hard-coded magic numbers.
  useEffect(() => {
    const el = mobileHeaderRef.current;
    if (!el) return;
    const update = () => {
      document.documentElement.style.setProperty(
        '--ebook-subheader-h',
        `${el.offsetHeight}px`,
      );
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Click-outside-to-close the mobile drawer. Skip clicks inside the drawer
  // itself or the subheader (which owns the toggle button — letting that
  // through would close-then-reopen on tap).
  useEffect(() => {
    if (!sidebarOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (mobileSidebarRef.current?.contains(target)) return;
      if (mobileHeaderRef.current?.contains(target)) return;
      setSidebarOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [sidebarOpen]);

  const handleToggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Mobile Header (supplied by consuming app). Wrapped so we can
          measure its height and align the drawer flush below it. */}
      {mobileHeader && (
        <div ref={mobileHeaderRef}>
          {mobileHeader({ sidebarOpen, onToggleSidebar: handleToggleSidebar })}
        </div>
      )}

      <div className="flex min-h-screen">
        {/* Mobile Sidebar (slides in from the right, no backdrop overlay) */}
        <nav
          ref={(node) => {
            mobileSidebarRef.current = node;
            // Match existing behavior: hand the scroll-progress hook this
            // element only while the drawer is open (matches the prior
            // `sidebarOpen ? navRef : undefined` semantics).
            if (sidebarOpen) {
              (navRef as React.MutableRefObject<HTMLElement | null>).current = node;
            }
          }}
          className={`w-[280px] bg-gray-50 border-l border-gray-200 fixed top-[calc(var(--header-h,0px)+var(--ebook-subheader-h,0px))] right-0 h-[calc(100vh-var(--header-h,0px)-var(--ebook-subheader-h,0px))] overflow-y-auto py-6 z-[100] transition-transform lg:hidden ${
            sidebarOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          {sidebarHeader}
          <ul className="space-y-1">
            {chapters.map((chapter) => (
              <li key={chapter.id}>
                {chapter.part && (
                  <div className="px-6 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
                    {chapter.part}
                  </div>
                )}
                <a
                  href={`#${chapter.id}`}
                  data-section={chapter.id}
                  onClick={() => setSidebarOpen(false)}
                  className={`block px-6 py-2 text-sm border-l-[3px] transition-colors cursor-pointer ${
                    activeSection === chapter.id
                      ? 'bg-white text-blue-600 border-blue-600 font-medium'
                      : 'border-transparent text-gray-700 hover:bg-white hover:text-blue-600'
                  }`}
                >
                  {chapter.title}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {/* Desktop Sidebar (sticky, in-flow) — width animates between 0 and 280px */}
        <div
          className={`hidden lg:block shrink-0 sticky top-0 h-screen overflow-hidden transition-[width] duration-300 ease-out ${
            desktopCollapsed ? 'w-0' : 'w-[280px]'
          }`}
          aria-hidden={desktopCollapsed}
        >
          <nav
            ref={navRef}
            className="w-[280px] bg-gray-50 border-r border-gray-200 h-screen overflow-y-auto py-8 relative"
          >
            <button
              type="button"
              onClick={() => setDesktopCollapsed(true)}
              className="absolute top-4 right-3 w-7 h-7 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-200 flex items-center justify-center transition-colors cursor-pointer"
              aria-label="Hide table of contents"
              tabIndex={desktopCollapsed ? -1 : 0}
            >
              <PanelLeftClose size={16} />
            </button>
            {sidebarHeader}
            <ul className="space-y-1">
              {chapters.map((chapter) => (
                <li key={chapter.id}>
                  {chapter.part && (
                    <div className="px-6 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
                      {chapter.part}
                    </div>
                  )}
                  <a
                    href={`#${chapter.id}`}
                    data-section={chapter.id}
                    tabIndex={desktopCollapsed ? -1 : 0}
                    className={`block px-6 py-2 text-sm border-l-[3px] transition-colors cursor-pointer ${
                      activeSection === chapter.id
                        ? 'bg-white text-blue-600 border-blue-600 font-medium'
                        : 'border-transparent text-gray-700 hover:bg-white hover:text-blue-600'
                    }`}
                  >
                    {chapter.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        {/* Persistent desktop "Chapters" pill — visible when the TOC is
            collapsed so readers can always re-open it. Offset down by the
            standard nav height so it clears the sticky site header. */}
        <button
          type="button"
          onClick={() => setDesktopCollapsed(false)}
          aria-label="Show table of contents"
          tabIndex={desktopCollapsed ? 0 : -1}
          className={`fixed left-4 z-[60] hidden lg:flex items-center gap-1.5 bg-white border border-gray-200 rounded-full shadow-md px-3 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 transition-colors cursor-pointer ${
            desktopCollapsed ? '' : 'lg:hidden'
          }`}
          style={{ top: 'calc(var(--header-h, 0px) + 1rem)' }}
        >
          <Menu size={14} aria-hidden="true" />
          <span>Chapters</span>
        </button>

        {/* Main Content */}
        <main className="flex-1 min-w-0 min-h-screen">
          {children}
          {footer}
        </main>
      </div>
    </div>
  );
}
